/* eslint-disable no-unreachable */
import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateMarketOutcomes } from '@/store/markets'
import { matchPath, useLocation } from 'react-router-dom'
import { accountUrl } from '@/urls'
import { pathnameWithoutLocalePrefix } from '@/util/localePath'
import { fetchCurrentUserBets, selectUserBets } from '@/store/wagers'
import constants from '@/constants'
import { selectActiveCurrency } from '@/store/app'
import { addNewOtherUserCommentToCommentsList, fetchCommentsSuccess } from '@/store/comments'
import useCurrentRouteName from './useCurrentRouteName'
import { ensureSharedPusher, getSharedPusherSync } from '@/services/sharedPusherClient'
import { selectCurrentUser } from '@/store/account'

const logReceived = (eventName, data) => {
  if (!import.meta.env.DEV) return
  console.log(`%c[market-pusher] ${eventName} RECEIVED`, 'color: #2563eb; font-weight: bold', data)
}

// Callback registry for market price change handlers
const marketPriceChangeCallbacks = new Map()

export const registerMarketPriceChangeCallback = (marketId, callback) => {
  if (callback) {
    marketPriceChangeCallbacks.set(marketId, callback)
  }
  return () => {
    marketPriceChangeCallbacks.delete(marketId)
  }
}

const useRealTimeMarket = () => {
  const dispatch = useDispatch()
  const activeCurrency = useSelector(selectActiveCurrency)
  const currentUser = useSelector(selectCurrentUser)
  const currentPage = useCurrentRouteName()
  const location = useLocation()

  const activeBets = useSelector(selectUserBets(constants.PLAY_MONEY, true))
  const pastBets = useSelector(selectUserBets(constants.PLAY_MONEY, true))
  const isMarketPage = currentPage === 'Market page'

  // Store current values in refs for use in event handlers
  const locationRef = useRef(location)
  const activeBetsRef = useRef(activeBets)
  const pastBetsRef = useRef(pastBets)
  const isMarketPageRef = useRef(isMarketPage)
  const activeCurrencyRef = useRef(activeCurrency)

  // Update refs when values change
  useEffect(() => {
    locationRef.current = location
    activeBetsRef.current = activeBets
    pastBetsRef.current = pastBets
    isMarketPageRef.current = isMarketPage
    activeCurrencyRef.current = activeCurrency
  }, [location, activeBets, pastBets, isMarketPage, activeCurrency])

  // Extract questionId from route using React Router
  const getMarketId = () => {
    const path = pathnameWithoutLocalePrefix(locationRef.current.pathname)
    const match =
      matchPath({ path: '/q/:questionId' }, path) || matchPath({ path: '/q/:questionId/:questionSlug' }, path)
    return match?.params?.questionId || null
  }

  useEffect(() => {
    if (import.meta.env.NODE_ENV === 'test') {
      return
    }

    if (!import.meta.env.VITE_PUSHER_APP_KEY || !import.meta.env.VITE_PUSHER_APP_CLUSTER) {
      return
    }

    let cancelled = false

    ;(async () => {
      const pusher = await ensureSharedPusher(currentUser?.id ?? null)
      if (cancelled || !pusher) return

      const channelId = `event`
      const pusherChannel = pusher.subscribe(channelId)

      pusherChannel.bind('price-change', data => {
        logReceived('price-change', data)
        const isInPortfolio = matchPath({ path: accountUrl() }, locationRef.current.pathname)

        if (isInPortfolio) {
          // if there is a wagers for this market
          if ([...activeBetsRef.current, ...pastBetsRef.current].find(bet => bet.question.id === data.id))
            dispatch(fetchCurrentUserBets({ question: data.id }))
        } else {
          // dispatch(fetchMarketById(data.id ,false))
          dispatch(
            updateMarketOutcomes({
              marketId: data.id,
              data,
              activeCurrency: activeCurrencyRef.current === 'OOM' ? constants.PLAY_MONEY : constants.REAL_MONEY,
            })
          )
        }

        // Check if we're on a market page for this specific question and refresh chart
        if (isMarketPageRef.current && getMarketId() === data.id.toString()) {
          const callback = marketPriceChangeCallbacks.get(data.id.toString())
          if (callback && data.markets) {
            callback(data.markets)
          }
        }
      })

      pusherChannel.bind('market-comment', data => {
        logReceived('market-comment', data)
        if (isMarketPageRef.current) {
          dispatch(addNewOtherUserCommentToCommentsList(getMarketId(), data))
        } else dispatch(fetchCommentsSuccess({ results: [{ ...data }], fetchBehavior: 'loadMore' }))
      })
    })()

    return () => {
      cancelled = true
      try {
        getSharedPusherSync()?.unsubscribe('event')
      } catch {
        /* noop */
      }
    }
  }, [dispatch, currentUser?.id])
}

export default useRealTimeMarket
